import { useSyncExternalStore } from 'react'
import { tcgCardUrl, tcgBulkBuyUrl, isTcgRepresentable } from './tcgplayer'
import { manapoolCardUrl, manapoolBulkBuyUrl, nonBasicDeckLines } from './manapool'
import { cardkingdomCardUrl, cardkingdomBulkBuyUrl } from './cardkingdom'
import { hareruyaCardUrl, HARERUYA_LIST_URL } from './hareruya'
import { cardmarketCardUrl, CARDMARKET_LIST_URL } from './cardmarket'
import { ligamagicCardUrl, LIGAMAGIC_LIST_URL } from './ligamagic'
import { inferredRegion, inferredRegionalStore } from './region'
import {
  getEnabledStores,
  subscribeStores,
  isRegionalStore,
  KNOWN_STORES,
  type RegionalStoreId,
  type StoreId,
} from '../api/stores'
import { trackEvent, getClientId } from '../lib/analytics'

export type CardVendor = StoreId
/** The stores whose deck-level buy is a pre-filled cart URL. */
export type DirectBulkVendor = Exclude<StoreId, RegionalStoreId>

/** Shared bulk-buy shape. TCGPlayer bulk sells by name, falling back to a
 *  product id for two-faced cards; Mana Pool and Card Kingdom bulk keep
 *  every non-basic name. */
export interface PurchaseCard {
  name: string
  quantity?: number
  tcgplayerId?: number | null
}

/** Device-level preference like a theme choice, NOT user data - deliberately
 *  absent from useAuth's signout-clear list so it survives sign-out. */
const STORAGE_KEY = 'cb_card_vendor'

/** The pick made on this page if persisting it failed (private mode). Without
 *  it, a failed write made the next snapshot re-read storage and silently
 *  drop the user's choice. */
let memPick: CardVendor | null = null

const listeners = new Set<() => void>()

/**
 * The vendor the user last picked, whether or not that store is currently
 * enabled. Unrecognised stored values read as TCGPlayer. Link building must
 * NOT use this directly: see getCardVendor.
 */
export function getSavedCardVendor(): CardVendor {
  // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
  return readSavedVendor() ?? 'tcgplayer'
}

/** The user's explicit pick, or null when they have never picked (or the
 *  stored value is garbage). Null is what lets the default split and the
 *  regional default tell a browser with no preference apart from one that
 *  chose TCGPlayer. */
export function readSavedVendor(): CardVendor | null {
  if (memPick) return memPick
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return (KNOWN_STORES as readonly string[]).includes(raw ?? '') ? (raw as CardVendor) : null
  } catch {
    return null
  }
}

/**
 * DEFAULT-STORE SPLIT TEST (build flag VITE_STORE_DEFAULT_SPLIT=1, off by
 * default; started 2026-09-30).
 *
 * Measured before it: TCGPlayer was the default only because the code falls
 * back to it, and over 60 days it earned ~$0.16 per buy click against Mana
 * Pool's ~$0.24, while Card Kingdom (5%, but nothing until a month clears
 * 10 referrals or $2,000) had one click since its flip. The PRIMARY outcome
 * is buy clicks per assigned client id by arm; revenue is read from each
 * program's own reporting over the same window, never inferred from clicks.
 *
 * Rules, each load-bearing:
 * - Applies ONLY to a browser with no saved pick AND no inferred regional
 *   store (utils/region.ts), both read synchronously and FROZEN in
 *   primeStoreArm. An explicit choice always wins, so nobody who chose a
 *   store is moved. A region-mapped browser is out of the split even while
 *   its regional store is dark (or before /stores answers): it gets
 *   TCGPlayer then, never an arm. Regional eligibility deliberately ignores
 *   whether the store is enabled, because /stores has not answered at prime
 *   time (plan 2026-10-04, section 5.2).
 * - The arms are PINNED to SPLIT_STORES, never KNOWN_STORES: adding the
 *   regional ids to KNOWN_STORES must not move any client id to another arm
 *   (plan section 5.1).
 * - The arm is a function of the durable analytics client_id
 *   (armForClientId), primed once in bootstrap BEFORE the first render
 *   (primeStoreArm), so the render-time getter only reads a cached value.
 *   Every tab sharing a client id agrees with no coordination, no arm is stored,
 *   and SQL can recompute any client's arm. Codex r1 found the earlier
 *   random-draw-then-store design let two tabs opened together disagree.
 *   (Two tabs opened at the same instant on a brand-new browser can still
 *   each mint a different client_id - a pre-existing analytics race - but
 *   each tab's events carry the id its arm came from, so arm and outcome
 *   stay consistent per id; it only double-counts a person.)
 * - An arm whose store is not enabled yet (before /stores answers, or if the
 *   API darks a store) reads as TCGPlayer, the same rule a saved pick
 *   follows. The exposure event records the effective vendor after /stores
 *   resolves, so that fallback is visible in the read.
 * - Exposure is recorded once per SESSION from bootstrap
 *   (recordStoreArmExposure), so a lost event costs one session, never the
 *   browser's denominator. Dedupe by client_id in SQL. It carries `region`,
 *   the inferred region, which is null for every eligible browser by
 *   construction.
 * - No window (the prerender): no arm, no event.
 *
 * SQL for a client's arm (matches armForClientId for ANY string, including a
 * corrupted stored id; arms in SPLIT_STORES order):
 *   (('x' || lpad(right(regexp_replace(client_id, '[^0-9a-fA-F]', '', 'g'), 8), 16, '0'))::bit(64)::bigint % 3)
 *   0 = tcgplayer, 1 = manapool, 2 = cardkingdom
 */
const EXPOSED_KEY = 'cb_store_arm_exposed'
let exposedThisPage = false

function splitEnabled(): boolean {
  return import.meta.env.VITE_STORE_DEFAULT_SPLIT === '1'
}

/** The split's arms, in the order the SQL above numbers them. PINNED: this
 *  list (not KNOWN_STORES, which grows) decides every client id's arm, so a
 *  new store can never reshuffle the running test. */
export const SPLIT_STORES = ['tcgplayer', 'manapool', 'cardkingdom'] as const satisfies readonly StoreId[]

/** Map a client id to an arm by its last 8 hex digits. crypto.randomUUID()
 *  ids end in fully random bits, so the arms are balanced to within one of
 *  2^32 suffixes. */
export function armForClientId(clientId: string): StoreId {
  const hex = clientId.replace(/[^0-9a-f]/gi, '').slice(-8)
  const n = hex ? parseInt(hex, 16) : 0
  return SPLIT_STORES[n % 3]
}

let cachedArm: StoreId | null = null
/** Eligibility FROZEN at prime time: true iff the browser had no saved pick
 *  and no inferred regional store when its arm was resolved. Codex r3:
 *  re-checking the live preference at exposure time dropped anyone who
 *  switched stores before /stores answered, a differential loss from the
 *  denominator. Null until primed. */
let eligibleAtPrime: boolean | null = null

/** The eligibility rule itself, on synchronous inputs only. */
function eligibleNow(): boolean {
  return readSavedVendor() === null && inferredRegionalStore() === null
}

/** Whether this browser is in the split: the primed answer, or (no prime,
 *  component tests) the live one. */
function splitEligible(): boolean {
  return eligibleAtPrime ?? eligibleNow()
}

/** Resolve and cache this browser's arm. Called from bootstrap before the
 *  first render so the snapshot never mints a client id itself. A no-op
 *  with the flag off or without a window. */
export function primeStoreArm(): void {
  if (!splitEnabled() || typeof window === 'undefined') return
  cachedArm = armForClientId(getClientId())
  eligibleAtPrime = eligibleNow()
}

/** This browser's split arm. Null when the split is off or there is no
 *  window. Reads the primed value; only resolves it here if bootstrap did
 *  not run (component tests). */
export function getStoreArm(): StoreId | null {
  if (!splitEnabled() || typeof window === 'undefined') return null
  if (!cachedArm) cachedArm = armForClientId(getClientId())
  return cachedArm
}

/**
 * Record that this client id is in the split, once per session. Called from
 * bootstrap AFTER /stores resolves so `effective_vendor` is what the user
 * saw. Eligibility is the value frozen by primeStoreArm, never the live
 * preference: a visitor who switches before /stores answers still counts
 * in the arm they were shown (intent-to-treat). Without a prime (component
 * tests) it falls back to the live check.
 */
export function recordStoreArmExposure(): void {
  const arm = getStoreArm()
  if (!arm || !splitEligible() || exposedThisPage) return
  try {
    if (sessionStorage.getItem(EXPOSED_KEY)) { exposedThisPage = true; return }
    sessionStorage.setItem(EXPOSED_KEY, '1')
  } catch {
    // private mode: the page-level guard still limits it to once per load
  }
  exposedThisPage = true
  trackEvent('store_arm_exposure', { arm, effective_vendor: getCardVendor(), region: inferredRegion() })
}

/** Test-only: forget per-page split state and any in-memory pick. */
export function __resetStoreArmForTests(): void {
  exposedThisPage = false
  cachedArm = null
  eligibleAtPrime = null
  memPick = null
}

/**
 * The EFFECTIVE vendor (plan 2026-10-04, section 4), first match wins:
 *   1. the saved pick, if it is a known id the API currently enables;
 *   2. the inferred regional store, if there is one and it is enabled;
 *   3. the default-split arm, if this browser is split-eligible and the arm's
 *      store is enabled;
 *   4. TCGPlayer.
 * Nothing here writes the preference, so a user who picked a store that is
 * dark right now gets it back the moment it is enabled again, rather than
 * being silently moved forever by one outage.
 */
export function getCardVendor(): CardVendor {
  const enabled = getEnabledStores()
  const saved = readSavedVendor()
  if (saved && enabled.includes(saved)) return saved
  const regional = inferredRegionalStore()
  if (regional && enabled.includes(regional)) return regional
  const arm = splitEligible() ? getStoreArm() : null
  if (arm && enabled.includes(arm)) return arm
  // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
  return 'tcgplayer'
}

/**
 * The stores the picker lists (plan section 4): the enabled non-regional
 * stores, plus the inferred regional store and the saved regional store when
 * each is enabled. The second covers a saved Cardmarket pick made inside its
 * region by a visitor who is now outside it. A dark store is never listed.
 * Order: the enabled list's own order.
 */
export function pickerStores(
  enabled: readonly StoreId[],
  inferred: RegionalStoreId | null,
  saved: StoreId | null,
): StoreId[] {
  return enabled.filter(s => !isRegionalStore(s) || s === inferred || s === saved)
}

export function setCardVendor(vendor: CardVendor): void {
  try {
    localStorage.setItem(STORAGE_KEY, vendor)
    memPick = null
  } catch {
    // private mode - remember the pick for this page view
    memPick = vendor
  }
  listeners.forEach(l => l())
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  const unsubStores = subscribeStores(cb)
  return () => { listeners.delete(cb); unsubStores() }
}

/** Reactive effective vendor - re-renders subscribers when the picker changes
 *  OR when the enabled-store list arrives from the API. */
export function useCardVendor(): CardVendor {
  // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
  return useSyncExternalStore(subscribe, getCardVendor, () => 'tcgplayer' as CardVendor)
}

export function vendorLabel(vendor: CardVendor): string {
  switch (vendor) {
    // eslint-disable-next-line i18next/no-literal-string -- vendor brand name
    case 'hareruya': return 'Hareruya'
    // eslint-disable-next-line i18next/no-literal-string -- vendor brand name
    case 'cardmarket': return 'Cardmarket'
    // eslint-disable-next-line i18next/no-literal-string -- vendor brand name
    case 'ligamagic': return 'LigaMagic'
    // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
    case 'manapool': return 'Mana Pool'
    // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
    case 'cardkingdom': return 'Card Kingdom'
    // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
    case 'tcgplayer': return 'TCGPlayer'
  }
}

/** Analytics event name per vendor; all count as buy clicks in the admin funnel. */
export function vendorClickEvent(vendor: CardVendor): string {
  switch (vendor) {
    // eslint-disable-next-line i18next/no-literal-string -- analytics event
    case 'hareruya': return 'hareruya_click'
    // eslint-disable-next-line i18next/no-literal-string -- analytics event
    case 'cardmarket': return 'cardmarket_click'
    // eslint-disable-next-line i18next/no-literal-string -- analytics event
    case 'ligamagic': return 'ligamagic_click'
    // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
    case 'manapool': return 'manapool_click'
    // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
    case 'cardkingdom': return 'cardkingdom_click'
    // eslint-disable-next-line i18next/no-literal-string -- vendor code, vendor brand name or analytics event
    case 'tcgplayer': return 'tcgplayer_click'
  }
}

export function vendorCardUrl(vendor: CardVendor, cardName: string, tcgplayerId?: number | null): string {
  switch (vendor) {
    case 'manapool': return manapoolCardUrl(cardName)
    case 'cardkingdom': return cardkingdomCardUrl(cardName)
    case 'tcgplayer': return tcgCardUrl(cardName, tcgplayerId)
    case 'hareruya': return hareruyaCardUrl(cardName)
    case 'cardmarket': return cardmarketCardUrl(cardName)
    case 'ligamagic': return ligamagicCardUrl(cardName)
  }
}

/** A cart-fill URL, for the stores that have one. Callers go through
 *  vendorBulkAction, which also covers the regional stores. */
export function vendorBulkBuyUrl(vendor: DirectBulkVendor, cards: PurchaseCard[]): string {
  switch (vendor) {
    case 'manapool': return manapoolBulkBuyUrl(cards)
    case 'cardkingdom': return cardkingdomBulkBuyUrl(cards)
    case 'tcgplayer': return tcgBulkBuyUrl(cards)
  }
}

/** The page a regional store takes a pasted list on (the phase 0 URLs, plan
 *  section 2). */
export function regionalListUrl(vendor: RegionalStoreId): string {
  switch (vendor) {
    case 'hareruya': return HARERUYA_LIST_URL
    case 'cardmarket': return CARDMARKET_LIST_URL
    case 'ligamagic': return LIGAMAGIC_LIST_URL
  }
}

/**
 * What a deck-level buy does for a vendor:
 * - `direct-url`: one link to a pre-filled cart (TCGPlayer, Mana Pool, Card
 *   Kingdom);
 * - `copy-and-open`: the regional stores, which have no verified cart-fill
 *   URL. `lines` are the "N Name" lines to copy (quantities summed per name,
 *   basics dropped) and `openUrl` is the store's list-import page.
 * Every caller renders from this instead of building a bulk URL itself.
 */
export type BulkBuyAction =
  | { kind: 'direct-url'; url: string }
  | { kind: 'copy-and-open'; vendor: RegionalStoreId; lines: string[]; openUrl: string }

export function vendorBulkAction(vendor: CardVendor, cards: PurchaseCard[]): BulkBuyAction {
  if (isRegionalStore(vendor)) {
    const text = nonBasicDeckLines(cards)
    return {
      // eslint-disable-next-line i18next/no-literal-string -- action kind code
      kind: 'copy-and-open',
      vendor,
      lines: text ? text.split('\n') : [],
      openUrl: regionalListUrl(vendor),
    }
  }
  // eslint-disable-next-line i18next/no-literal-string -- action kind code
  return { kind: 'direct-url', url: vendorBulkBuyUrl(vendor, cards) }
}

/** True for the regional stores: no affiliate link, no cart-fill URL, and
 *  only a US price to show. */
export function isRegionalVendor(vendor: CardVendor): vendor is RegionalStoreId {
  return isRegionalStore(vendor)
}

/** What a buy click bought: 'card' is one card's link; 'bulk' and 'unowned'
 *  are the deck-level controls. */
export type BuyClickType = 'card' | 'bulk' | 'unowned'

/**
 * THE buy-click logger (plan section 7). Every buy link on the site,
 * per-card and deck-level, calls this, so each one counts in the admin funnel
 * as `<store>_click` with `{type, card_count, source?, region}`. `source` is
 * omitted (not merely undefined) when unset, so untagged surfaces keep their
 * payload shape. `region` is the inferred ISO code or null: a heuristic,
 * never a buyer country.
 */
export function trackBuyClick(
  vendor: CardVendor,
  type: BuyClickType,
  cardCount: number,
  source?: string,
): void {
  const props: Record<string, string | number | null> = { type, card_count: cardCount }
  if (source) props.source = source
  props.region = inferredRegion()
  trackEvent(vendorClickEvent(vendor), props)
}

/** Cards actually purchasable via the given vendor's bulk link (basics are
 *  filtered by the URL builders; this is the representability split).
 *
 *  This MUST use the same predicate the TCGPlayer builder uses. Callers derive
 *  the displayed count, the price and the "only N are available" copy from it,
 *  so a looser prefilter would promise a complete match for a cart that
 *  silently contains fewer cards. Mana Pool, Card Kingdom and the regional
 *  stores take every name (the Card Kingdom redirect front-faces two-faced
 *  names server-side). */
export function purchasableCards(vendor: CardVendor, cards: PurchaseCard[]): PurchaseCard[] {
  // The regional stores take a copied list, which keeps every non-basic name
  // (the Mana Pool rule).
  return vendor === 'tcgplayer' ? cards.filter(isTcgRepresentable) : cards
}
